import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Formik, Form } from 'formik';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import zxcvbn from 'zxcvbn';
import history from 'utils/history';
import PasswordInput from 'atoms/PasswordInput';
import { makeSelectIsMobile } from 'containers/App/selectors';
import logoLogin from 'images/logo_login.svg';
import loadIcon from 'images/load-icon.gif';
import { registerReferrer } from './actions';
import { makeSelectLoading, makeSelectError, makeSelectSuccess } from './selectors';
import 'containers/SignUp/index.css';

function ReferrerSignUp({ loading, error, success, doRegister, isMobile }) {
	// Cache zxcvbn by password string. Formik calls validate on every keystroke
	// of every field, and zxcvbn is synchronous and slow (50-500ms). Caching
	// avoids recomputing for the same password on unrelated keystrokes.
	const zxcvbnCacheRef = useRef({ password: null, result: null });
	const getZxcvbnResult = (password) => {
		if (!password) return null;
		if (zxcvbnCacheRef.current.password === password) {
			return zxcvbnCacheRef.current.result;
		}
		const result = zxcvbn(password);
		zxcvbnCacheRef.current = { password, result };
		return result;
	};

	const validate = (values) => {
		const errors = {};
		// eslint-disable-next-line
		const emailReg = /^\w+([\+\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
		const phoneReg = /^[+]?[(]?[0-9]{3}[)]?[-s.]?[0-9]{3}[-s.]?[0-9]{4,6}$/m;

		if (!values.name) errors.name = 'Please enter your name.';
		if (!values.email) {
			errors.email = 'Please enter your email.';
		} else if (!emailReg.test(values.email)) {
			errors.email = 'Invalid email address.';
		}
		if (!values.phone) {
			errors.phone = 'Please enter your phone number.';
		} else if (!phoneReg.test(values.phone)) {
			errors.phone = 'Invalid phone number.';
		}
		if (!values.password) {
			errors.password = 'Please enter a password.';
		} else {
			// Pure: just read cached zxcvbn for the strength error. No setState.
			const result = getZxcvbnResult(values.password);
			if (result.score < 3) {
				errors.password = 'Please enter a stronger password.';
			}
		}
		if (!values.confirmPassword) {
			errors.confirmPassword = 'Re-enter your password.';
		} else if (values.password && values.confirmPassword !== values.password) {
			errors.confirmPassword = 'Password does not match.';
		}
		return errors;
	};

	const getPasswordBarStyle = (password, passwordScore) => {
		if (!password) return { height: '5px', width: '0%' };
		let strColor = 'red';
		let strWidth = '1%';
		switch (passwordScore) {
			case 0:
				strColor = 'red';
				strWidth = '1%';
				break;
			case 1:
				strColor = 'orange';
				strWidth = '25%';
				break;
			case 2:
				strColor = 'yellow';
				strWidth = '50%';
				break;
			case 3:
				strColor = '#5cff47';
				strWidth = '75%';
				break;
			case 4:
				strColor = 'green';
				strWidth = '100%';
				break;
			default:
		}
		return {
			backgroundColor: strColor,
			height: '5px',
			width: strWidth,
			transition: 'all 300ms ease-in-out',
		};
	};

	return (
		<div className="container-fluid p-0 d-flex h-100vh bg-white overflow-hidden">
			{!isMobile && (
				<div className="p-0 image-login-container container-fluid" />
			)}
			<div
				className={`${
					isMobile ? 'col' : 'col-6'
				} d-flex align-self-center flex-column px-xl-5 overflow-auto h-100`}
			>
				<div className="d-flex justify-content-center mb-4">
					<img src={logoLogin} alt="" />
				</div>
				{success ? (
					<div className="px-5 text-center">
						<div className="font-30 font-weight-bold mb-3">Check Your Email</div>
						<p className="font-13 text-muted">
							We&apos;ve sent a verification link to your email address. Please click the link to verify your account.
						</p>
						<p className="font-13 text-muted">
							Once verified, your account will be reviewed by our team and you&apos;ll receive an email when it&apos;s approved.
						</p>
						<span
							className="link-color font-weight-bold hand font-13"
							onClick={() => history.push('/referrer/signin')}
						>
							Go to Sign In
						</span>
					</div>
				) : (
					<>
						{error && (
							<div className="d-flex justify-content-center error-font">
								{error}
							</div>
						)}
						<div className="mt-2 px-5 d-flex justify-content-center text-center w-100 font-30 font-weight-bold">
							Referrer SignUp
						</div>
						<Formik
					initialValues={{
						name: '',
						email: '',
						phone: '',
						credentials: '',
						practiceName: '',
						practiceAddress: '',
						practicePhone: '',
						practiceZipCode: '',
						password: '',
						confirmPassword: '',
					}}
					validate={validate}
					onSubmit={(values) => doRegister(values)}
				>
					{({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => {
						const zxcvbnResult = getZxcvbnResult(values.password);
						const passwordScore = zxcvbnResult?.score ?? 0;
						const passwordFeedback = zxcvbnResult?.feedback?.suggestions || [];
						return (
						<Form className="w-100">
							<div className="px-5">
								<label
									htmlFor="InputName"
									className="text-uppercase form-font font-10"
								>
									NAME *
								</label>
								<input
									value={values.name}
									onChange={handleChange}
									onBlur={handleBlur}
									type="text"
									name="name"
									className={`form-control ${
										errors.name && touched.name ? 'is-invalid' : ''
									}`}
									id="InputName"
									placeholder="Dr. Jane Smith"
								/>
								{errors.name && touched.name && (
									<div className="invalid-feedback">{errors.name}</div>
								)}
							</div>
							<div className="mt-2 px-5">
								<label
									htmlFor="InputEmail"
									className="text-uppercase form-font font-10"
								>
									EMAIL *
								</label>
								<input
									value={values.email}
									onChange={handleChange}
									onBlur={handleBlur}
									type="email"
									name="email"
									className={`form-control ${
										errors.email && touched.email ? 'is-invalid' : ''
									}`}
									id="InputEmail"
									placeholder="Enter Email"
								/>
								{errors.email && touched.email && (
									<div className="invalid-feedback">{errors.email}</div>
								)}
							</div>
							<div className="mt-2 px-5">
								<label
									htmlFor="phone"
									className="text-uppercase form-font font-10"
								>
									PHONE *
								</label>
								<PhoneInput
									maxLength={20}
									placeholder="Enter Phone Number"
									value={values.phone}
									international={false}
									country="US"
									className={`form-control ${
										errors.phone && touched.phone ? 'is-invalid' : ''
									}`}
									onChange={(number) => setFieldValue('phone', number || '')}
									onBlur={() => handleBlur({ target: { name: 'phone' } })}
								/>
								{errors.phone && touched.phone && (
									<div className="invalid-feedback">{errors.phone}</div>
								)}
							</div>
							<div className="mt-2 px-5">
								<label
									htmlFor="InputCredentials"
									className="text-uppercase form-font font-10"
								>
									CREDENTIALS
								</label>
								<input
									value={values.credentials}
									onChange={handleChange}
									onBlur={handleBlur}
									type="text"
									name="credentials"
									className="form-control"
									id="InputCredentials"
									placeholder="e.g. MD, PhD, LCSW"
								/>
							</div>
							<div className="mt-2 px-5">
								<label
									htmlFor="InputPracticeName"
									className="text-uppercase form-font font-10"
								>
									PRACTICE NAME
								</label>
								<input
									value={values.practiceName}
									onChange={handleChange}
									onBlur={handleBlur}
									type="text"
									name="practiceName"
									className="form-control"
									id="InputPracticeName"
									placeholder="Practice Name"
								/>
							</div>
							<div className="mt-2 px-5">
								<label
									htmlFor="InputPracticeAddress"
									className="text-uppercase form-font font-10"
								>
									PRACTICE ADDRESS
								</label>
								<input
									value={values.practiceAddress}
									onChange={handleChange}
									onBlur={handleBlur}
									type="text"
									name="practiceAddress"
									className="form-control"
									id="InputPracticeAddress"
									placeholder="Practice Address"
								/>
							</div>
							<div className="mt-2 px-5">
								<label
									htmlFor="InputPracticePhone"
									className="text-uppercase form-font font-10"
								>
									PRACTICE PHONE
								</label>
								<input
									value={values.practicePhone}
									onChange={handleChange}
									onBlur={handleBlur}
									type="text"
									name="practicePhone"
									className="form-control"
									id="InputPracticePhone"
									placeholder="Practice Phone"
								/>
							</div>
							<div className="mt-2 px-5">
								<label
									htmlFor="InputPracticeZipCode"
									className="text-uppercase form-font font-10"
								>
									PRACTICE ZIP CODE
								</label>
								<input
									value={values.practiceZipCode}
									onChange={handleChange}
									onBlur={handleBlur}
									type="text"
									name="practiceZipCode"
									className="form-control"
									id="InputPracticeZipCode"
									placeholder="Zip Code"
								/>
							</div>
							<div className="mt-2 px-5">
								<PasswordInput
									label={
										<label
											htmlFor="InputPassword"
											className="text-uppercase form-font font-10"
										>
											PASSWORD *
										</label>
									}
									value={values.password}
									onChange={handleChange}
									name="password"
									className={`form-control ${
										errors.password && touched.password ? 'is-invalid' : ''
									}`}
									id="InputPassword"
									placeholder="Enter Password"
								/>
								<div style={getPasswordBarStyle(values.password, passwordScore)} />
								{errors.password && touched.password && (
									<div className="invalid-feedback d-block">
										{errors.password}
										{passwordFeedback.map((suggestion, i) => (
											<div key={i}>{suggestion}</div>
										))}
									</div>
								)}
							</div>
							<div className="mt-2 px-5">
								<PasswordInput
									label={
										<label
											htmlFor="ConfirmPassword"
											className="text-uppercase form-font font-10"
										>
											CONFIRM PASSWORD *
										</label>
									}
									value={values.confirmPassword}
									onChange={handleChange}
									name="confirmPassword"
									className={`form-control ${
										errors.confirmPassword && touched.confirmPassword
											? 'is-invalid'
											: ''
									}`}
									id="ConfirmPassword"
									placeholder="Confirm Your Password"
								/>
								{errors.confirmPassword && touched.confirmPassword && (
									<div className="invalid-feedback d-block">
										{errors.confirmPassword}
									</div>
								)}
							</div>
							<div className="d-flex justify-content-between mt-3 px-5">
								<button
									type="submit"
									className="btn btn-primary btn-lg btn-block btn-color text-uppercase font-15"
									disabled={loading}
								>
									{loading ? (
										<img src={loadIcon} alt="Loading..." height="23px" />
									) : (
										<span>SIGN UP</span>
									)}
								</button>
							</div>
							<div className="mt-4 px-5 d-flex justify-content-center already-have-an-acco font-13">
								Already have an account?
								<span
									className="ml-1 link-color font-weight-bold hand"
									onClick={() => history.push('/referrer/signin')}
								>
									Sign In
								</span>
							</div>
						</Form>
					);
					}}
						</Formik>
					</>
				)}
			</div>
		</div>
	);
}

const mapStateToProps = createStructuredSelector({
	loading: makeSelectLoading(),
	error: makeSelectError(),
	success: makeSelectSuccess(),
	isMobile: makeSelectIsMobile(),
});

function mapDispatchToProps(dispatch) {
	return {
		doRegister: (data) => dispatch(registerReferrer(data)),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(ReferrerSignUp);
